// src/components/projects/ProjectForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Button,
  Typography,
  Paper,
  Divider,
  FormHelperText,
  Autocomplete,
  useTheme
} from '@mui/material';
import { 
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Assignment as TemplateIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import useClientStore from '../../stores/clientStore';
import { Card } from '../../design-system';

// Project types
const PROJECT_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'website_development', label: 'Website Development' },
  { value: 'content_creation', label: 'Content Creation' },
  { value: 'social_media_management', label: 'Social Media Management' },
  { value: 'branding', label: 'Branding' },
  { value: 'rebranding', label: 'Rebranding' },
  { value: 'marketing_campaign', label: 'Marketing Campaign' },
  { value: 'advertising_campaign', label: 'Advertising Campaign' },
  { value: 'video_production', label: 'Video Production' },
  { value: 'event_management', label: 'Event Management' },
  { value: 'other', label: 'Other' }
];

// Project statuses
const PROJECT_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

// Project priorities
const PROJECT_PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

// Available regions
const REGIONS = [
  'Egypt',
  'Saudi Arabia',
  'Qatar',
  'UAE',
  'Kuwait',
  'Bahrain',
  'Oman',
  'Global',
  'Other'
];

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Project name is required'),
  clientId: Yup.string().required('Client is required'),
  type: Yup.string().required('Project type is required'),
  priority: Yup.string().required('Priority is required'),
//   'dates.plannedStartDate': Yup.date().required('Start date is required'),
//   'dates.plannedEndDate': Yup.date()
//     .required('End date is required')
//     .min(
//       Yup.ref('dates.plannedStartDate'),
//       'End date must be after start date'
//     )
});

/**
 * Project form component for creating and editing projects
 */
const ProjectForm = ({ 
  project = null, 
  onSubmit, 
  onCancel,
  isSubmitting = false,
  selectedTemplate = null,
  duplicateProject = null
}) => {
  const theme = useTheme();
  const { clients, fetchClients, isLoading: isLoadingClients } = useClientStore();
  const [tagsInput, setTagsInput] = useState('');
  
  // Load clients if not already loaded
  useEffect(() => {
    if (!clients.length) {
      fetchClients();
    }
  }, [fetchClients, clients.length]);
  
  // Initialize default values
  const getInitialValues = () => {
    if (project) {
      return {
        name: project.name || '',
        description: project.description || '',
        clientId: project.clientId?._id || project.clientId || '',
        brandId: project.brandId?._id || project.brandId || '',
        type: project.type || 'general',
        status: project.status || 'draft',
        priority: project.priority || 'medium',
        dates: {
          plannedStartDate: project.dates?.plannedStartDate 
            ? new Date(project.dates.plannedStartDate) 
            : null,
          plannedEndDate: project.dates?.plannedEndDate 
            ? new Date(project.dates.plannedEndDate) 
            : null
        },
        regions: project.regions || [],
        tags: project.tags || [],
        magicalElements: {
          transformationNarrative: project.magicalElements?.transformationNarrative || '',
          enchantmentLevel: project.magicalElements?.enchantmentLevel || 'moderate',
          specialFeatures: project.magicalElements?.specialFeatures || []
        },
        notes: project.notes || ''
      };
    }
    
    // Default values for new project
    return {
      name: '',
      description: '',
      clientId: '',
      brandId: '',
      type: 'general',
      status: 'draft',
      priority: 'medium',
      dates: {
        plannedStartDate: new Date(),
        plannedEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
      },
      regions: [],
      tags: [],
      magicalElements: {
        transformationNarrative: '',
        enchantmentLevel: 'moderate',
        specialFeatures: []
      },
      notes: ''
    };
  };
  
  // Setup formik
  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    }
  });
  
  // Handle adding a tag
  const handleAddTag = () => {
    if (!tagsInput.trim()) return;
    
    const newTag = tagsInput.trim();
    if (!formik.values.tags.includes(newTag)) {
      formik.setFieldValue('tags', [...formik.values.tags, newTag]);
    }
    setTagsInput('');
  };
  
  // Handle removing a tag
  const handleDeleteTag = (tagToDelete) => {
    formik.setFieldValue(
      'tags',
      formik.values.tags.filter(tag => tag !== tagToDelete)
    );
  };
  
  // Get client brands
  const getClientBrands = () => {
    const selectedClient = clients.find(
      client => client._id === formik.values.clientId
    );
    return selectedClient?.brands || [];
  };
  
  // Handle client change - reset brand if needed
  const handleClientChange = (event) => {
    const clientId = event.target.value;
    formik.setFieldValue('clientId', clientId);
    
    // Reset brand if client changes
    formik.setFieldValue('brandId', '');
  };
  
  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        {/* Template/Duplicate Info */}
        {(selectedTemplate || duplicateProject) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'primary.100'
                }}>
                  {selectedTemplate ? 
                    <TemplateIcon sx={{ color: 'primary.main' }} /> : 
                    <CopyIcon sx={{ color: 'primary.main' }} />
                  }
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {selectedTemplate ? 'Creating from Template' : 'Duplicating Project'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTemplate ? 
                      `Using template: ${selectedTemplate.name}` : 
                      `Duplicating from: ${duplicateProject.name}`
                    }
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Basic Information */}
        <Grid item xs={12}>
          <Card 
            level={1} 
            title="Basic Information"
            magical={true}
            sx={{ mb: 3 }}
          >
            <Grid container spacing={2} sx={{ p: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Project Name"
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth
                  error={formik.touched.clientId && Boolean(formik.errors.clientId)}
                >
                  <InputLabel id="client-label">Client</InputLabel>
                  <Select
                    labelId="client-label"
                    id="clientId"
                    name="clientId"
                    value={formik.values.clientId}
                    onChange={handleClientChange}
                    onBlur={formik.handleBlur}
                    label="Client"
                    required
                  >
                    {clients.map(client => (
                      <MenuItem key={client._id} value={client._id}>
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.clientId && formik.errors.clientId && (
                    <FormHelperText>{formik.errors.clientId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="brand-label">Brand</InputLabel>
                  <Select
                    labelId="brand-label"
                    id="brandId"
                    name="brandId"
                    value={formik.values.brandId}
                    onChange={formik.handleChange}
                    label="Brand"
                    disabled={!formik.values.clientId}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {getClientBrands().map(brand => (
                      <MenuItem key={brand._id} value={brand._id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl 
                  fullWidth
                  error={formik.touched.type && Boolean(formik.errors.type)}
                >
                  <InputLabel id="type-label">Project Type</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Project Type"
                    required
                  >
                    {PROJECT_TYPES.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.type && formik.errors.type && (
                    <FormHelperText>{formik.errors.type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Description"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
        
        {/* Project Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Project Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth
                  error={formik.touched.status && Boolean(formik.errors.status)}
                >
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    label="Status"
                  >
                    {PROJECT_STATUSES.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth
                  error={formik.touched.priority && Boolean(formik.errors.priority)}
                >
                  <InputLabel id="priority-label">Priority</InputLabel>
                  <Select
                    labelId="priority-label"
                    id="priority"
                    name="priority"
                    value={formik.values.priority}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Priority"
                    required
                  >
                    {PROJECT_PRIORITIES.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.priority && formik.errors.priority && (
                    <FormHelperText>{formik.errors.priority}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={formik.values.dates.plannedStartDate}
                  onChange={(date) => 
                    formik.setFieldValue('dates.plannedStartDate', date)
                  }
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth
                      required
                      error={
                        formik.touched['dates.plannedStartDate'] && 
                        Boolean(formik.errors['dates.plannedStartDate'])
                      }
                      helperText={
                        formik.touched['dates.plannedStartDate'] && 
                        formik.errors['dates.plannedStartDate']
                      }
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={formik.values.dates.plannedEndDate}
                  onChange={(date) => 
                    formik.setFieldValue('dates.plannedEndDate', date)
                  }
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      fullWidth
                      required
                      error={
                        formik.touched['dates.plannedEndDate'] && 
                        Boolean(formik.errors['dates.plannedEndDate'])
                      }
                      helperText={
                        formik.touched['dates.plannedEndDate'] && 
                        formik.errors['dates.plannedEndDate']
                      }
                    />
                  )}
                  minDate={formik.values.dates.plannedStartDate}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="notes"
                  name="notes"
                  label="Notes"
                  variant="outlined"
                  multiline
                  rows={3}
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Tags and Regions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Tags & Regions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="regions"
                  options={REGIONS}
                  value={formik.values.regions}
                  onChange={(_, newValue) => {
                    formik.setFieldValue('regions', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Target Regions"
                      placeholder="Select regions"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {formik.values.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleDeleteTag(tag)}
                        
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Add Tag"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddTag}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Magical Elements (optional) */}
        <Grid item xs={12}>
          <Card level={2} magical sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Magical Elements
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="magicalElements.transformationNarrative"
                  name="magicalElements.transformationNarrative"
                  label="Transformation Narrative"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formik.values.magicalElements.transformationNarrative}
                  onChange={formik.handleChange}
                  helperText="Describe how this project will transform the client's business or brand"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="enchantment-level-label">Enchantment Level</InputLabel>
                  <Select
                    labelId="enchantment-level-label"
                    id="magicalElements.enchantmentLevel"
                    name="magicalElements.enchantmentLevel"
                    value={formik.values.magicalElements.enchantmentLevel}
                    onChange={formik.handleChange}
                    label="Enchantment Level"
                  >
                    <MenuItem value="subtle">Subtle</MenuItem>
                    <MenuItem value="moderate">Moderate</MenuItem>
                    <MenuItem value="prominent">Prominent</MenuItem>
                  </Select>
                  <FormHelperText>
                    How prominently should magical elements appear in this project
                  </FormHelperText>
                </FormControl>
                
                <Autocomplete
                  multiple
                  freeSolo
                  id="magicalElements.specialFeatures"
                  options={[]}
                  value={formik.values.magicalElements.specialFeatures}
                  onChange={(_, newValue) => {
                    formik.setFieldValue('magicalElements.specialFeatures', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Special Magical Features"
                      placeholder="Add special features"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip 
                        variant="outlined" 
                        label={option} 
                        {...getTagProps({ index })} 
                      />
                    ))
                  }
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
        
        {/* Form Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onCancel}
              startIcon={<CancelIcon />}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              
              startIcon={<SaveIcon />}
              disabled={isSubmitting}
            >
              {project ? 'Update Project' : 'Create Project'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default ProjectForm;